import React, { useState, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://bulknet-platform-production.up.railway.app';

const TEMPLATE_HEADERS = ['property_address', 'property_name', 'tenant_first_name', 'tenant_last_name', 'apartment_number', 'phone', 'email'];
const EXAMPLE_ROW = ['123 Main St, Brooklyn NY 11201', 'Main Street Apartments', 'John', 'Smith', '2B', '212-555-1234', 'john.smith@email.com'];
const VACANT_ROW  = ['123 Main St, Brooklyn NY 11201', 'Main Street Apartments', 'Vacant', '', '3A', '', ''];

function downloadTemplate() {
  const rows = [TEMPLATE_HEADERS, EXAMPLE_ROW, VACANT_ROW];
  const csv = rows.map(r => r.map(v => '"' + v + '"').join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bulknet_tenant_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function ImportTenantsModal({ onClose, onSuccess }) {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInput = useRef();

  function handleFile(f) {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) { setError('Please upload a .csv, .xlsx, or .xls file.'); return; }
    setError('');
    setFile(f);
  }

  async function handleUpload() {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(API + '/api/tenants/bulk-upload', {
        method: 'POST',
        headers: token ? { Authorization: 'Bearer ' + token } : {},
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setResult(data);
      setStep('done');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>📥 Bulk Import Tenants</h2>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {step === 'upload' && (
          <div style={styles.body}>
            <div style={styles.templateBox}>
              <span style={{ fontSize: 14, color: '#555' }}>Download the template to get started.</span>
              <button onClick={downloadTemplate} style={styles.templateBtn}>⬇ Download Template</button>
            </div>

            <div style={styles.colTable}>
              {[
                { col: 'property_name',     req: false, note: 'Building or complex name' },
                { col: 'property_address',  req: true,  note: 'Must match an existing property' },
                { col: 'tenant_first_name', req: true,  note: 'Use "Vacant" for empty units' },
                { col: 'tenant_last_name',  req: true },
                { col: 'apartment_number',  req: true,  note: 'e.g. 2B, 101' },
                { col: 'phone',             req: true,  note: 'e.g. 212-555-1234' },
                { col: 'email',             req: true },
              ].map(({ col, req, note }) => (
                <div key={col} style={styles.colRow}>
                  <code style={styles.colName}>{col}</code>
                  <span style={{ ...styles.badge, background: req ? '#fee2e2' : '#f0fdf4', color: req ? '#b91c1c' : '#166534' }}>
                    {req ? 'Required' : 'Optional'}
                  </span>
                  {note && <span style={styles.colNote}>{note}</span>}
                </div>
              ))}
            </div>

            <div style={styles.infoBox}>
              💡 A temporary password is auto-generated for each tenant. Units are created automatically if they don't exist.
              For vacant units, enter <strong>Vacant</strong> as the first name — the unit will be enrolled under the landlord's name.
            </div>

            <div
              style={{ ...styles.dropzone, ...(dragOver ? styles.dropzoneActive : {}) }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => fileInput.current.click()}
            >
              <input ref={fileInput} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
              {file ? (
                <div>
                  <div style={{ fontSize: 32 }}>📄</div>
                  <div style={{ fontWeight: 600, marginTop: 8 }}>{file.name}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{(file.size / 1024).toFixed(1)} KB · Click to change</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 36 }}>☁️</div>
                  <div style={{ fontWeight: 600, marginTop: 8 }}>Drop your file here</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>or click to browse · CSV, XLSX, XLS</div>
                </div>
              )}
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.footer}>
              <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
              <button onClick={handleUpload} disabled={!file || loading} style={{ ...styles.primaryBtn, opacity: (!file || loading) ? 0.5 : 1 }}>
                {loading ? 'Uploading…' : 'Import Tenants'}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div style={styles.body}>
            <div style={styles.resultBox}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>{result.created > 0 || result.vacant > 0 ? '✅' : '⚠️'}</div>
              <h3 style={{ margin: '0 0 16px', fontSize: 20 }}>Import Complete</h3>
              <div style={styles.statsRow}>
                <div style={styles.stat}>
                  <div style={{ ...styles.statNum, color: '#16a34a' }}>{result.created}</div>
                  <div style={styles.statLabel}>Tenants Created</div>
                </div>
                <div style={styles.stat}>
                  <div style={{ ...styles.statNum, color: '#f59e0b' }}>{result.vacant}</div>
                  <div style={styles.statLabel}>Vacant Units</div>
                </div>
                <div style={styles.stat}>
                  <div style={{ ...styles.statNum, color: '#dc2626' }}>{result.skipped}</div>
                  <div style={styles.statLabel}>Skipped</div>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div style={styles.errorsTable}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Row-level errors:</div>
                  {result.errors.map((e, i) => (
                    <div key={i} style={styles.errRow}>
                      <span style={styles.errRowNum}>Row {e.row}</span>
                      <span>{e.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={styles.footer}>
              <button onClick={() => { setStep('upload'); setFile(null); setResult(null); }} style={styles.cancelBtn}>Import Another File</button>
              <button onClick={onClose} style={styles.primaryBtn}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb' },
  title: { margin: 0, fontSize: 20, fontWeight: 700 },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' },
  body: { padding: '20px 24px 24px' },
  templateBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 16px', marginBottom: 16 },
  templateBtn: { background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  colTable: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },
  colRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  colName: { background: '#f3f4f6', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', minWidth: 180 },
  badge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99 },
  colNote: { color: '#6b7280', fontSize: 12 },
  infoBox: { fontSize: 12, color: '#6b7280', marginBottom: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 },
  dropzone: { border: '2px dashed #d1d5db', borderRadius: 10, padding: '36px 24px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', background: '#fafafa', marginBottom: 16, color: '#374151' },
  dropzoneActive: { borderColor: '#3b82f6', background: '#eff6ff' },
  errorBox: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  cancelBtn: { background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer', fontWeight: 500 },
  primaryBtn: { background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  resultBox: { textAlign: 'center', padding: '16px 0' },
  statsRow: { display: 'flex', justifyContent: 'center', gap: 32, margin: '16px 0 20px' },
  stat: { textAlign: 'center' },
  statNum: { fontSize: 40, fontWeight: 800 },
  statLabel: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  errorsTable: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 14, textAlign: 'left', maxHeight: 200, overflowY: 'auto' },
  errRow: { display: 'flex', gap: 10, fontSize: 13, padding: '4px 0', borderBottom: '1px solid #fee2e2' },
  errRowNum: { fontWeight: 700, color: '#dc2626', minWidth: 55 },
};
